import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../../../services/api';
import { useNotification } from '../../../contexts/NotificationContext';
import { usePageLoaderContext } from '../../../contexts/PageLoaderContext';
import { useConfirm } from '../../../contexts/ConfirmContext';
import SidePanel from '../../UI/SidePanel';
import CounsellingList from './CounsellingList';
import CounsellingForm, { CounsellingFormData } from './CounsellingForm';
import { CounsellingRecord } from './CounsellingCard';
import CustomDropdown, { DropdownOption } from '../../UI/CustomDropdown';
import styles from '../../../styles/CounsellingManagement.module.css';
import taskManagementStyles from '../../../styles/TaskManagement.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import searchBarStyles from '../../../styles/SearchBar.module.css';
import customDropdownStyles from '../../../styles/CustomDropdown.module.css';

const CounsellingManagement: React.FC = () => {
  const notification = useNotification();
  const { showLoader, hideLoader } = usePageLoaderContext();
  const confirmService = useConfirm();

  const [counsellings, setCounsellings] = useState<CounsellingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCounselling, setEditingCounselling] = useState<CounsellingRecord | null>(null);
  const [selectedCounselling, setSelectedCounselling] = useState<CounsellingRecord | null>(null);

  const [filters, setFilters] = useState({
    search: '',
    academic_year: '',
    year: '',
    semester: '',
  });
  const [academicYearOptions, setAcademicYearOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    loadCounsellings();
  }, [filters]);

  // Load academic years
  useEffect(() => {
    const loadAcademicYears = async () => {
      try {
        const response: any = await apiService.get('/api/academic/academic-years/');
        const years = Array.isArray(response) ? response : (response.results || response.data || []);
        const options: DropdownOption[] = [
          { value: '', label: 'All Academic Years' },
          ...years.map((year: any) => ({
            value: year.id.toString(),
            label: year.name
          }))
        ];
        setAcademicYearOptions(options);
      } catch (err) {
        console.error('Error loading academic years:', err);
      }
    };
    loadAcademicYears();
  }, []);

  const loadCounsellings = async () => {
    setLoading(true);
    setIsInitialLoad(true);
    try {
      const params: any = {};
      if (filters.search) params.search = filters.search;
      if (filters.academic_year) params.academic_year = filters.academic_year;
      if (filters.year) params.year = filters.year;
      if (filters.semester) params.semester = filters.semester;

      const response: any = await apiService.get('/api/counselling/', { params });
      // Handle both paginated and non-paginated responses
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setCounsellings(data);
      setError(null);
    } catch (err: any) {
      console.error('Error loading counsellings:', err);
      setError(err);
      notification.error(err.response?.data?.message || 'Failed to load counselling records');
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleAddCounselling = () => {
    setEditingCounselling(null);
    setShowAddModal(true);
  };

  const handleEditCounselling = (counselling: CounsellingRecord) => {
    setEditingCounselling(counselling);
    setShowAddModal(true);
  };

  const handleDeleteCounselling = async (counselling: CounsellingRecord) => {
    const confirmed = await confirmService.show(
      'Delete Counselling Record',
      `Are you sure you want to delete the counselling record for ${counselling.student_name || 'this student'}? This action cannot be undone.`
    );

    if (!confirmed) return;

    showLoader('Deleting counselling record...');
    try {
      await apiService.delete(`/api/counselling/${counselling.id}/`);
      setCounsellings(prev => prev.filter(c => c.id !== counselling.id));
      notification.success('Counselling record deleted successfully');
    } catch (err: any) {
      console.error('Error deleting counselling:', err);
      notification.error(err.response?.data?.message || 'Failed to delete counselling record');
    } finally {
      hideLoader();
    }
  };

  const handleSubmitForm = async (data: CounsellingFormData) => {
    showLoader(editingCounselling ? 'Updating counselling record...' : 'Creating counselling record...');
    try {
      // Map form data to backend format
      const payload = {
        academic_year: data.academic_year || null,
        student_registration_number: data.student_registration_number || '',
        student_name: data.student_name || '',
        year: data.year,
        semester: data.semester,
        transport_mode: data.transportMode || '',
        journey_time: data.journeyTime || '',
        have_textbooks: data.haveTextBooks || 'No',
        difficult_subjects: data.difficultSubjects || '',
        difficult_subjects_reason: data.difficultSubjectsReason || '',
        easy_subjects: data.easySubjects || '',
        easy_subjects_reason: data.easySubjectsReason || '',
        attendance_marks: data.attendanceMarks || [],
        attendance_months: data.attendanceMonths || [],
        remedial_suggestions: data.remedialSuggestions || '',
        study_hours: data.studyHours || '',
        record_hours: data.recordHours || '',
        entertainment_hours: data.entertainmentHours || '',
        counselling_suggestions: data.counsellingSuggestions || '',
        best_friends: data.bestFriends || '',
        parent_interaction: data.parentInteraction || '',
        faculty_encouragement: data.facultyEncouragement || '',
        disturbances: data.disturbances || 'No',
        counsellor_comments: data.counsellorComments || [],
      };

      if (editingCounselling) {
        await apiService.put(`/api/counselling/${editingCounselling.id}/`, payload);
        notification.success('Counselling record updated successfully');
      } else {
        await apiService.post('/api/counselling/', payload);
        notification.success('Counselling record created successfully');
      }

      // Reload counsellings
      await loadCounsellings();
      setShowAddModal(false);
      setEditingCounselling(null);
    } catch (err: any) {
      console.error('Error submitting form:', err);
      let errorMessage = 'Failed to save counselling record';

      if (err.response?.data) {
        if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (typeof err.response.data === 'object') {
          // Extract the first error message from field-specific errors
          const fieldErrors = Object.values(err.response.data);
          if (fieldErrors.length > 0) {
            const firstError = fieldErrors[0];
            errorMessage = Array.isArray(firstError) ? firstError[0] : (typeof firstError === 'string' ? firstError : errorMessage);
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      notification.error(errorMessage);
      throw err; // Re-throw to let form handle error display
    } finally {
      hideLoader();
    }
  };

  const handleDownloadReport = async (counselling: CounsellingRecord) => {
    showLoader('Generating report...');
    try {
      const blob = await apiService.downloadCounsellingReport(counselling.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Counselling_Report_${counselling.student_registration_number || counselling.student_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      notification.success('Report downloaded successfully');
    } catch (err: any) {
      console.error('Error downloading report:', err);
      notification.error('Failed to download report');
    } finally {
      hideLoader();
    }
  };

  const handleCounsellingClick = (counselling: CounsellingRecord) => {
    setSelectedCounselling(counselling);
    // TODO: Open details view or side panel
  };

  const filteredCounsellings = useMemo(() => {
    let filtered = [...counsellings];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(c =>
        c.student_name?.toLowerCase().includes(searchLower) ||
        c.student_registration_number?.toLowerCase().includes(searchLower) ||
        c.difficult_subjects?.toLowerCase().includes(searchLower) ||
        c.easy_subjects?.toLowerCase().includes(searchLower) ||
        (Array.isArray(c.counsellor_comments) &&
          c.counsellor_comments.some(comment =>
            comment.comment?.toLowerCase().includes(searchLower)
          ))
      );
    }

    if (filters.academic_year) {
      filtered = filtered.filter(c => c.academic_year?.toString() === filters.academic_year);
    }

    if (filters.year) {
      filtered = filtered.filter(c => c.year === filters.year);
    }

    if (filters.semester) {
      filtered = filtered.filter(c => c.semester === filters.semester);
    }

    return filtered;
  }, [counsellings, filters]);

  const yearOptions: DropdownOption[] = [
    { value: '', label: 'All Years' },
    { value: 'I', label: 'Year I' },
    { value: 'II', label: 'Year II' },
    { value: 'III', label: 'Year III' },
    { value: 'IV', label: 'Year IV' },
  ];

  const semesterOptions: DropdownOption[] = [
    { value: '', label: 'All Semesters' },
    { value: 'I', label: 'Semester I' },
    { value: 'II', label: 'Semester II' },
  ];

  return (
    <div className={styles.counsellingManagementContainer}>
      {/* Fixed Header */}
      <div className={styles.fixedHeader}>
        <div className={styles.headerContent}>
          <div className={searchBarStyles.searchFilterContainer}>
            <div className={searchBarStyles.searchBar}>
              <input
                type="text"
                placeholder="Search"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
              <button type="submit">
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                onClick={handleAddCounselling}
                title="Add Counselling"
                style={{ whiteSpace: 'nowrap' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <span className={styles.btnText}>Add Counselling</span>
              </button>

              <div>
                <CustomDropdown
                  options={academicYearOptions}
                  value={filters.academic_year}
                  onChange={(value: string | string[]) => handleFilterChange('academic_year', Array.isArray(value) ? value[0] : value)}
                  placeholder="Filter by Academic Year"
                  size="sm"
                  variant="outlined"
                  className="filter"
                />
              </div>

              <div>
                <CustomDropdown
                  options={yearOptions}
                  value={filters.year}
                  onChange={(value: string | string[]) => handleFilterChange('year', Array.isArray(value) ? value[0] : value)}
                  placeholder="Filter by Year"
                  size="sm"
                  variant="outlined"
                  className="filter"
                />
              </div>

              <div>
                <CustomDropdown
                  options={semesterOptions}
                  value={filters.semester}
                  onChange={(value: string | string[]) => handleFilterChange('semester', Array.isArray(value) ? value[0] : value)}
                  placeholder="Filter by Semester"
                  size="sm"
                  variant="outlined"
                  className="filter"
                />
              </div>

              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnIcon}`}
                onClick={loadCounsellings}
                title="Refresh"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <polyline points="1 20 1 14 7 14"></polyline>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className={styles.contentSection} style={{ marginTop: '120px' }}>
        <CounsellingList
          counsellings={filteredCounsellings}
          loading={loading}
          error={error}
          isInitialLoad={isInitialLoad}
          onCounsellingClick={handleCounsellingClick}
          onRetry={loadCounsellings}
          onEdit={handleEditCounselling}
          onDelete={handleDeleteCounselling}
          onDownload={handleDownloadReport}
          showEditDelete={true}
          emptyStateTitle="No Counselling Records Found"
          emptyStateDescription="You haven't created any counselling records yet. Click 'Add Counselling' to get started."
        />
      </div>

      {/* Add/Edit Form Side Panel */}
      <SidePanel
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingCounselling(null);
        }}
        title={editingCounselling ? 'Edit Counselling' : 'Add Counselling for Student'}
        icon={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        }
      >
        <CounsellingForm
          initialData={editingCounselling ? {
            academic_year: editingCounselling.academic_year?.toString() || '',
            student_registration_number: editingCounselling.student_registration_number || '',
            student_name: editingCounselling.student_name,
            year: editingCounselling.year,
            semester: editingCounselling.semester,
            transportMode: editingCounselling.transport_mode || '',
            journeyTime: editingCounselling.journey_time || '',
            haveTextBooks: editingCounselling.have_textbooks || 'No',
            difficultSubjects: editingCounselling.difficult_subjects || '',
            difficultSubjectsReason: editingCounselling.difficult_subjects_reason || '',
            easySubjects: editingCounselling.easy_subjects || '',
            easySubjectsReason: editingCounselling.easy_subjects_reason || '',
            attendanceMarks: editingCounselling.attendance_marks || [],
            attendanceMonths: editingCounselling.attendance_months || [],
            remedialSuggestions: editingCounselling.remedial_suggestions || '',
            studyHours: editingCounselling.study_hours || '',
            recordHours: editingCounselling.record_hours || '',
            entertainmentHours: editingCounselling.entertainment_hours || '',
            counsellingSuggestions: editingCounselling.counselling_suggestions || '',
            bestFriends: editingCounselling.best_friends || '',
            parentInteraction: editingCounselling.parent_interaction || '',
            facultyEncouragement: editingCounselling.faculty_encouragement || '',
            disturbances: editingCounselling.disturbances || 'No',
            counsellorComments: editingCounselling.counsellor_comments || [],
          } : undefined}
          onSubmit={handleSubmitForm}
          onCancel={() => {
            setShowAddModal(false);
            setEditingCounselling(null);
          }}
        />
      </SidePanel>
    </div>
  );
};

export default CounsellingManagement;
